import { forwardRef, ReactNode, SelectHTMLAttributes } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils";
import { ButtonProps } from "../button";

const selectVariants = cva(
  [
    "al-flex al-items-center al-justify-between al-rounded-md",
    "al-text-sm al-font-normal",
    "al-transition-colors",
    "focus-visible:al-outline-none focus-visible:al-ring-2 focus-visible:al-ring-ring focus-visible:al-ring-offset-2",
    "disabled:al-pointer-events-none disabled:al-opacity-50",
    "[&>span]:al-line-clamp-1",
    // Override base styles
    "!al-bg-transparent",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "!al-bg-primary !al-text-brand !al-border-primary",
          "hover:!al-bg-primary/90 hover:!al-text-white",
          "focus:!al-bg-primary/90 focus:!al-text-white",
        ].join(" "),
        destructive: [
          "!al-bg-destructive !al-text-destructive-foreground",
          "hover:!al-bg-destructive/90",
          "focus:!al-bg-destructive/90",
        ].join(" "),
        outline: [
          "!al-border !al-border-input !al-bg-background",
          "hover:!al-bg-accent hover:!al-text-accent-foreground",
          "focus:!al-bg-accent focus:!al-text-accent-foreground",
        ].join(" "),
        success: [
          "!al-bg-success !al-text-success-foreground",
          "hover:!al-bg-success/90",
          "focus:!al-bg-success/90",
        ].join(" "),
        secondary: [
          "!al-bg-secondary !al-text-secondary-foreground !al-border-2 !al-border-secondary",
          "hover:!al-bg-secondary/80",
          "focus:!al-bg-secondary/80",
        ].join(" "),
        ghost: [
          "!al-bg-transparent !al-text-foreground",
          "hover:!al-bg-accent hover:!al-text-accent-foreground",
          "focus:!al-bg-accent focus:!al-text-accent-foreground",
        ].join(" "),
      },
      size: {
        default: "!al-h-10 !al-px-4 !al-py-2",
        sm: "!al-h-9 !al-px-3",
        lg: "!al-h-11 !al-px-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface Option {
  value: string;
  label: string;
}

type SelectAttributes = Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "size">;

interface NativeSelectProps extends SelectAttributes, VariantProps<typeof selectVariants> {
  options: Option[];
  onChange?: (value: string) => void;
  value?: string;
  placeholder?: string;
  icon?: ReactNode;
  open?: boolean;
  label?: string;
  showPlaceholder?: boolean;
  buttonProps?: ButtonProps;
}

export const NativeSelect = forwardRef<HTMLButtonElement, NativeSelectProps>(
  (
    {
      options,
      onChange,
      value,
      placeholder,
      className,
      disabled,
      icon,
      variant,
      size,
      open,
      label,
      showPlaceholder,
      buttonProps,
      id,
    },
    ref
  ) => {
    return (
      <Select value={value} onValueChange={onChange} disabled={disabled} open={open}>
        <SelectTrigger
          id={id}
          ref={ref}
          className={cn(selectVariants({ variant, size, className }))}
          {...buttonProps}
        >
          {icon && <div className="al-mr-2">{icon}</div>}
          {showPlaceholder && label ? (
            <div className="al-flex al-items-center al-gap-1">
              <span className="al-text-muted-foreground">{label}</span>
              <SelectValue placeholder={placeholder} />
            </div>
          ) : (
            <SelectValue placeholder={placeholder} />
          )}
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
);

NativeSelect.displayName = "NativeSelect";
